import moment from "moment"
import type { cellValueExcel, cellValuePdf, RowExportTypeExcel, RowExportTypePdf } from "types"
import { convertNumber } from "utils"
import type { ItemBuildReportRow } from "../types/itemBuildReport"
import {
  formatReportBarcode,
  getIngredientReportBarcode,
} from "./itemBuildReportBarcode"

export const ITEM_BUILD_EXPORT_COLUMNS = [
  { header: "NO", key: "no", width: 4 },
  { header: "DATE", key: "transaction_date", width: 8 },
  { header: "BUILD ID", key: "trx_code", width: 10 },
  { header: "CODE", key: "product_barcode", width: 10 },
  { header: "DESCRIPTION", key: "product_name", width: 14 },
  { header: "CATEGORY", key: "mr_product_category_name", width: 10 },
  { header: "QTY BUILD", key: "qty_build_unit", width: 7 },
  { header: "SOH", key: "soh", width: 8 },
  { header: "UNIT", key: "unit", width: 6 },
  { header: "COMPONENT AT COST", key: "component_at_cost", width: 10 },
  { header: "OVERHEAD", key: "overhead", width: 8 },
  { header: "AVERAGE PRICE", key: "avg_price", width: 9 },
  { header: "PIC", key: "employee_name", width: 10 },
  { header: "COMPONENT CODE", key: "component_code", width: 10 },
  { header: "COMPONENT DESCRIPTION", key: "component_description", width: 14 },
  { header: "USED", key: "qty_ingredient_unit", width: 6 },
  { header: "COMPONENT UNIT", key: "component_unit", width: 8 },
  { header: "PRICE", key: "component_avg_price", width: 8 },
  { header: "COST", key: "component_cost", width: 8 },
] as const

export function buildPeriodLabel(startDate: string, endDate: string): string {
  const start = moment(startDate)
  const end = moment(endDate)
  if (!start.isValid() || !end.isValid()) return ""
  if (start.isSame(end, "day")) return start.format("DD/MM/YYYY")
  return `${start.format("DD/MM/YYYY")} – ${end.format("DD/MM/YYYY")}`
}

const currencyPdf = (value: number): cellValuePdf => ({
  text: convertNumber(value).intoCurrency,
  style: { textAlign: "right", fontWeight: "bold" },
})

const currencyExcel = (value: number): cellValueExcel => ({
  text: value,
  style: {
    alignment: { horizontal: "right" },
    numFmt: "#,##0",
  },
})

function buildFlatRows(rows: ItemBuildReportRow[]) {
  const flatRows: {
    build: ItemBuildReportRow
    ingredient: ItemBuildReportRow["ingredients"][number] | null
    rowNumber: number
  }[] = []

  rows.forEach((row, index) => {
    const ingredients = row.ingredients?.length ? row.ingredients : [null]
    ingredients.forEach((ingredient) => {
      flatRows.push({
        build: row,
        ingredient,
        rowNumber: index + 1,
      })
    })
  })

  return flatRows
}

export function buildItemBuildExportPdfRows(
  rows: ItemBuildReportRow[]
): RowExportTypePdf[] {
  return buildFlatRows(rows).map(({ build, ingredient, rowNumber }) => {
    const unitLabel =
      build.unit_sell_measure_name ?? build.unit_measure_name ?? "-"

    return {
      no: rowNumber,
      transaction_date: build.transaction_date
        ? moment(build.transaction_date).format("DD/MM/YYYY")
        : "-",
      trx_code: build.trx_code ?? "-",
      product_barcode: formatReportBarcode(build.product_barcode),
      product_name: build.product_name ?? "-",
      mr_product_category_name: build.mr_product_category_name ?? "-",
      qty_build_unit: build.qty_build_unit ?? 0,
      soh: currencyPdf(Number(build.soh ?? 0)),
      unit: unitLabel,
      component_at_cost: currencyPdf(Number(build.component_at_cost ?? 0)),
      overhead: currencyPdf(Number(build.overhead ?? 0)),
      avg_price: currencyPdf(Number(build.avg_price ?? 0)),
      employee_name: build.employee_name ?? "-",
      component_code: getIngredientReportBarcode(ingredient),
      component_description: ingredient?.product_ingredient_name ?? "-",
      qty_ingredient_unit:
        ingredient?.qty_ingredient_unit != null
          ? ingredient.qty_ingredient_unit
          : "-",
      component_unit: ingredient?.mr_unit_name ?? "-",
      component_avg_price:
        ingredient != null
          ? currencyPdf(Number(ingredient.avg_price ?? 0))
          : "-",
      component_cost:
        ingredient != null
          ? currencyPdf(Number(ingredient.amount ?? 0))
          : "-",
    }
  })
}

export function buildItemBuildExportExcelRows(
  rows: ItemBuildReportRow[]
): RowExportTypeExcel[] {
  return buildFlatRows(rows).map(({ build, ingredient, rowNumber }) => {
    const unitLabel =
      build.unit_sell_measure_name ?? build.unit_measure_name ?? "-"

    return {
      no: rowNumber,
      transaction_date: build.transaction_date
        ? moment(build.transaction_date).format("DD/MM/YYYY")
        : "-",
      trx_code: build.trx_code ?? "-",
      product_barcode: formatReportBarcode(build.product_barcode),
      product_name: build.product_name ?? "-",
      mr_product_category_name: build.mr_product_category_name ?? "-",
      qty_build_unit: build.qty_build_unit ?? 0,
      soh: currencyExcel(Number(build.soh ?? 0)),
      unit: unitLabel,
      component_at_cost: currencyExcel(Number(build.component_at_cost ?? 0)),
      overhead: currencyExcel(Number(build.overhead ?? 0)),
      avg_price: currencyExcel(Number(build.avg_price ?? 0)),
      employee_name: build.employee_name ?? "-",
      component_code: getIngredientReportBarcode(ingredient),
      component_description: ingredient?.product_ingredient_name ?? "-",
      qty_ingredient_unit:
        ingredient?.qty_ingredient_unit != null
          ? ingredient.qty_ingredient_unit
          : "-",
      component_unit: ingredient?.mr_unit_name ?? "-",
      component_avg_price:
        ingredient != null
          ? currencyExcel(Number(ingredient.avg_price ?? 0))
          : "-",
      component_cost:
        ingredient != null
          ? currencyExcel(Number(ingredient.amount ?? 0))
          : "-",
    }
  })
}
